<template>
  <div class="linked-fields-textarea" :focus="focused">
    <linked-fields-tags-menu
      ref="tagsMenu"
      @cancel="onTagsBrowserCancel"
      @apply="linkedFieldSelected"
    />

    <div
      contentEditable="true"
      dir="auto"
      ref="editContainer"
      class="-edit-area"
      @mouseup="onMouseup"
      @keyup="onKeyup"
      @paste="onPaste"
      @focus="setFocused"
      @blur="setBlurred"
      :style="textAreaStyle"
      :data-ph="placeholder"
    ></div>

    <div class="-fake-caret" :style="fakeCaretStyle" />

    <div class="-controls">
      <div class="-checkbox-container">
        <wt-check-box
          :checked="mandatory"
          label-right="Linked data required"
          :disabled="!hasLinkedFields"
          @changed="mandatoryChanged"
        ></wt-check-box>
        <i
          v-if="hasLinkedFields"
          v-tooltip="{
            title:
              'In case of missing data, this scene will be skipped on automated videos.',
            placement: 'bottom',
            delay: 250,
          }"
          class="wticon wticon-help-alt co-tooltip"
        >
        </i>
      </div>

      <div
        class="-add-button -wt2-link wt-button hollow-dark"
        :class="{ '-active': addMoreActive }"
        v-if="showAddButton"
        @mouseup="onAddMore"
      >
        <i class="wticon wticon-link2" />Add
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import LinkedFieldsTagsMenu from 'components/linkedFields/LinkedFieldsTagsMenu.vue';

export default {
  name: 'LinkedFieldsTextarea',
  components: {
    LinkedFieldsTagsMenu,
  },
  props: {
    textValue: {
      type: Array,
      default: null,
    },
    originalTextValue: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    resizeable: {
      type: Boolean,
      default: false,
    },
    innerPadding: {
      type: Number,
      default: 10,
    },
    numberOfLines: {
      type: Number,
      default: 10,
    },
    showAddButton: {
      type: Boolean,
      default: true,
    },
    originalMandatory: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      text: '',
      tagClass: '-tag',
      focused: false,
      $currentTagEl: null,
      currentSRange: null,
      insertCaretRange: null,
      addMoreActive: false,
      mandatory: false,
    };
  },
  created() {
    this.text = this.textValue || this.originalTextValue;
    this.mandatory = this.originalMandatory;
  },
  mounted() {
    // create an observer instance
    this.observer = new MutationObserver((mutations) => {
      this.textChanged();
    });
    const config = { childList: true, characterData: true, attributes: true };
    this.fieldReadyNextTick && this.fieldReadyNextTick();

    const textValues = this.parseTextWithTags(this.text);
    textValues.forEach((t) => {
      let n;
      if (typeof t === 'object') {
        if (t.type === 'text') {
          n = document.createTextNode(t.text);
        } else if (t.type === 'linkedField') {
          n = this.renderTag(t.defaultText, t.linkedField).$el;
        }
      }
      n && this.$refs.editContainer.appendChild(n);
    });

    this.observer.observe(this.$refs.editContainer, config);

    document.addEventListener('mouseup', this.onDocumentMouseup);
    document.addEventListener('mousedown', this.onDocumentMousedown);
  },
  beforeDestroy() {
    this.observer.disconnect();
    document.removeEventListener('mouseup', this.onDocumentMouseup);
    document.addEventListener('mousedown', this.onDocumentMousedown);
  },
  methods: {
    onDocumentMouseup(e) {
      if (e.screenX > 0) {
        // this weird IF is due to weird collision with jquery-ui-wochit
        this.checkSelection();
      }
    },

    mandatoryChanged(value) {
      this.mandatory = value;
      this.$emit('mandatoryChanged', value);
    },

    onTagsBrowserCancel() {
      this.resetLinkedFieldsMenu(); // reset menu
    },

    linkedFieldSelected(linkedFieldsObj) {
      if (this.$currentTagEl) {
        if (!linkedFieldsObj.value) {
          // replace a tag with regular text
          this.$currentTagEl.parentNode.replaceChild(
            document.createTextNode(linkedFieldsObj.label),
            this.$currentTagEl
          );
        } else {
          this.$currentTagEl.innerText = linkedFieldsObj.value;
          this.$currentTagEl.setAttribute('value', linkedFieldsObj.value);
          this.$currentTagEl.setAttribute('label', linkedFieldsObj.label);
        }
      } else {
        if (!linkedFieldsObj.value || !linkedFieldsObj.label) {
          return;
        }
        if (this.insertCaretRange) {
          // insert in specific caret position
          const containerContent =
            this.insertCaretRange.commonAncestorContainer.textContent;
          const insertSpaceBefore =
            this.insertCaretRange.startOffset &&
            containerContent[this.insertCaretRange.startOffset - 1] !== ' ';
          const insertSpaceAfter =
            !containerContent.length || // empty content
            !containerContent[this.insertCaretRange.startOffset + 1] || // last charct index
            (containerContent[this.insertCaretRange.startOffset + 1] && // no space available after charct
              containerContent[this.insertCaretRange.startOffset + 1] !== ' ');
          const instance = this.renderTag(
            linkedFieldsObj.label,
            linkedFieldsObj.value
          );
          insertSpaceAfter &&
            this.insertCaretRange.insertNode(document.createTextNode(' '));
          this.insertCaretRange.insertNode(instance.$el);
          insertSpaceBefore &&
            this.insertCaretRange.insertNode(document.createTextNode(' '));
          this.insertCaretRange.collapse();
        } else {
          const sRange = this.currentSRange;
          const instance = this.renderTag(
            linkedFieldsObj.label,
            linkedFieldsObj.value
          );

          if (sRange) {
            // replace highlighted text with tag
            const addEmptyTextNode =
              sRange.endContainer.nodeType === Node.TEXT_NODE &&
              sRange.endOffset === sRange.endContainer.length &&
              !instance.$el.nextSibling;

            sRange.deleteContents();
            sRange.insertNode(instance.$el);
            sRange.collapse();
            if (addEmptyTextNode) {
              const textNode = document.createTextNode(' ');
              instance.$el.parentNode.appendChild(textNode);
              const range = document.createRange();
              const sel = window.getSelection();
              range.setStart(textNode, textNode.length);
              range.collapse(true);
              sel.removeAllRanges();
              sel.addRange(range);
            }
          } else {
            // insert new tag @ eol...
            this.$refs.editContainer.appendChild(document.createTextNode(' '));
            this.$refs.editContainer.appendChild(instance.$el);
            this.$refs.editContainer.appendChild(document.createTextNode(' '));
          }
        }
      }

      this.textChanged();
      this.resetLinkedFieldsMenu();
    },

    resetLinkedFieldsMenu() {
      this.$refs.tagsMenu.close();
      this.currentSRange = null;
      this.$currentTagEl = null;
      this.insertCaretRange = null;
      this.addMoreActive = false;
    },

    showLinkedFieldsMenu(options) {
      options = Object.assign(
        {
          label: null,
          value: null,
          showHighlight: true,
          actionLabel: 'Apply',
          focusLabel: false,
          editExisting: false,
        },
        options
      );
      this.$refs.tagsMenu.show(options);
    },

    renderTag(label, value) {
      const tagClass = this.tagClass;
      const ComponentClass = Vue.extend({
        render() {
          return this.$createElement(
            'div',
            {
              attrs: {
                contenteditable: false,
                readonly: true,
                label,
                value,
              },
              class: ['-tag-display', 'wt2-link', tagClass],
            },
            value
          );
        },
      });
      const instance = new ComponentClass();
      instance.$mount(); // pass nothing
      return instance;
    },

    getLinkedFieldsFromInnerDiv(el) {
      el.normalize();
      let text = '';
      const nodes = [...el.childNodes];
      const fields = [];
      nodes.forEach((n) => {
        if (n.nodeType === Node.TEXT_NODE) {
          fields.push({
            type: 'text',
            text: n.textContent,
          });
          text += n.textContent;
        }
        if (
          n.nodeType === Node.ELEMENT_NODE &&
          n.classList.contains(this.tagClass)
        ) {
          fields.push({
            type: 'linkedField',
            defaultText: n.getAttribute('label'),
            linkedField: n.getAttribute('value'),
          });
          text += n.getAttribute('label');
        }
      });

      return { fields, text };
    },

    getLinkedFields(el) {
      el.normalize();
      let plainTxt = '';
      const nodes = [...el.childNodes];
      const linkedFields = [];
      const pushLinkedField = (...fields) => {
        for (const field of fields) {
          if (!linkedFields.length || field.type !== 'text') {
            linkedFields.push(field);
            return;
          }
          const last = linkedFields[linkedFields.length - 1];
          if (last.type !== 'text') {
            linkedFields.push(field);
            return;
          }
          last.text += field.text;
        }
      };
      nodes.forEach((n) => {
        if (n.nodeType === Node.TEXT_NODE) {
          pushLinkedField({
            type: 'text',
            text: n.textContent,
          });
          plainTxt += n.textContent;
        }
        if (n.nodeType === Node.ELEMENT_NODE) {
          if (n.classList.contains(this.tagClass)) {
            pushLinkedField({
              type: 'linkedField',
              defaultText: n.getAttribute('label'),
              linkedField: n.getAttribute('value'),
            });
            plainTxt += n.getAttribute('label');
          } else {
            const { fields, text } = this.getLinkedFieldsFromInnerDiv(n);
            if (n.previousSibling) {
              pushLinkedField({ type: 'text', text: '\n' });
            }
            pushLinkedField(...fields);
            if (n.previousSibling) {
              plainTxt = plainTxt + '\n' + text;
            } else {
              plainTxt += text;
            }
          }
        }
      });

      return { linkedFields, plainTxt };
    },

    textChanged() {
      const { linkedFields, plainTxt } = this.getLinkedFields(
        this.$refs.editContainer
      );
      this.$emit('change', { linkedFields, plainTxt });
    },

    onPaste(e) {
      // allow pasting only plain text...
      e.preventDefault();
      const text = (e.originalEvent || e).clipboardData.getData('text/plain');
      document.execCommand('insertHTML', false, text);
    },
    parseTextWithTags(text) {
      let finalTextArray = [];
      if (typeof text === 'string') {
        // fallback regular text
        finalTextArray.push({
          type: 'text',
          text,
        });
      } else {
        finalTextArray = text;
      }
      return finalTextArray;
    },
    onKeyup(e) {
      if (e.keyCode != 27) {
        this.resetLinkedFieldsMenu();
        this.textChanged();
        this.checkSelection();
      }
    },
    onMouseup(e) {
      if (e.target.classList.contains(this.tagClass)) {
        this.$nextTick(() => {
          this.$currentTagEl = e.target;
          this.showLinkedFieldsMenu({
            label: e.target.getAttribute('label'),
            value: e.target.getAttribute('value'),
            targetRect: e.target.getBoundingClientRect(),
            editExisting: true,
          });
        });
      }
    },
    checkSelection() {
      const sel = window.getSelection();
      if (sel.rangeCount && this.$refs.editContainer.contains(sel.baseNode)) {
        const typedText = sel.toString();
        if (!typedText.trim().length) {
          return;
        }

        const sRange = sel.getRangeAt(0);
        const rangeContent = sRange.cloneContents();

        const containsLinebreaks = false; // tbd - /\r|\n/.exec(rangeContent.textContent);

        if (
          containsLinebreaks ||
          rangeContent.querySelector(`.${this.tagClass}`)
        ) {
          return; // disallow tags containing other tags...
        }

        const oRect = sRange.getBoundingClientRect();

        this.showLinkedFieldsMenu({
          label: typedText,
          value: null,
          targetRect: oRect,
          editExisting: false,
        });

        this.currentSRange = sRange;
      }
    },
    setFocused() {
      this.focused = true;
    },
    setBlurred(e) {
      this.$emit('blur', e);
      this.focused = false;
    },
    onAddMore(e) {
      const sel = window.getSelection();
      if (sel.focusNode && this.$refs.editContainer.contains(sel.focusNode)) {
        const sRange = sel.getRangeAt(0);
        this.insertCaretRange = sRange;
      } else {
        this.insertCaretRange = null;
      }
      this.showLinkedFieldsMenu({
        label: null,
        value: null,
        targetRect: e.target.getBoundingClientRect(),
        showHighlight: false,
        actionLabel: 'Insert',
        focusLabel: true,
        editExisting: false,
      });
      this.addMoreActive = true;
    },
  },
  computed: {
    hasLinkedFields() {
      if (this.textValue && this.textValue.length > 0) {
        return this.textValue.some((t) => t.type === 'linkedField');
      }
      return false;
    },
    textAreaStyle() {
      return {
        minHeight: `calc(${this.numberOfLines * 1.5}em + ${
          this.innerPadding
        }px)`,
        padding: this.innerPadding + 'px',
        overflowY: 'auto',
      };
    },
    fakeCaretStyle() {
      if (this.insertCaretRange) {
        const oRect = this.insertCaretRange.getBoundingClientRect();
        return {
          top: `${oRect.top}px`,
          left: `${oRect.left}px`,
        };
      } else {
        return {
          display: 'none',
        };
      }
    },
  },
  watch: {
    hasLinkedFields(newVal) {
      if (!newVal) {
        this.mandatory = false;
      }
    },
  },
};
</script>

<style lang="scss">
.-fake-caret {
  position: fixed;
  content: '|';
  top: 0;
  left: 0;
  width: 1px;
  height: 19px;
  border-left: 1px solid black;
  z-index: 999999;
  margin-left: 0px;
  margin-top: 0px;
}

.linked-fields-textarea {
  position: relative;
  display: inline-block;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.2);
  font-size: 0;
  background: white;

  .-checkbox-container {
    .co-tooltip {
      margin-left: 10px;
    }
    .-label-right {
      max-width: unset;
    }
    display: flex;
    align-items: center;
    font-size: 14px;
    margin-left: 10px;
    flex: 1;
    .wticon {
      right: unset;
    }
  }

  &.focused {
    border: 1px solid #06a9f4;
  }

  .-controls {
    display: flex;
    align-items: center;
  }

  .-add-button {
    padding: 7px 10px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    user-select: none;
    border: 1px solid rgba(0, 0, 0, 0.75);
    min-width: auto !important;
    font-size: 14px !important;
    margin-bottom: -1px;
    margin-right: -1px;

    &.-active {
      background: rgba(0, 0, 0, 0.05);
    }
    i {
      font-size: 17px;
      position: relative !important;
      transform: none !important;
    }
  }
}

.-edit-area {
  //important to keep inline blocked:
  //will keep nl instead of new divs on ENTER
  //line-height: 24px;
  white-space: pre-wrap;
  line-height: 1.5em;
  height: auto;
  width: 100%;
  font-weight: 400;
  outline: none;
  border: none;
  //font-size: 18px;
  font-size: 14px;

  &:empty:not(:focus):before {
    content: attr(data-ph);
    color: grey;
    font-style: italic;
  }

  &.-resizeable {
    resize: both;
  }

  .-tag-display {
    display: inline-block;
    user-select: none;
  }

  span {
    //background: blue; //rgba(255,255,255,0.3); //lighten(#46C8F5, 10%);
  }
}
</style>
